import { useCallback, useEffect, useMemo, useRef, useState, type ElementType } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  CheckCircle,
  Clock,
  Flame,
  Package,
  Printer,
  Users,
  Home,
  ShoppingBag,
  Truck,
  Undo2,
  Hourglass,
  AlertTriangle,
} from 'lucide-react'
import { useKdsUrgencyTimer, type UrgencyTier } from './useKdsUrgencyTimer'
import type { Order, OrderItem } from '@/types'
import { useQuery } from '@tanstack/react-query'
import { ENABLED_ORDER_TYPES_KEY, fetchEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { kitchenSoundService } from '@/services/soundService'
import { RecipeCardButton } from '@/components/recipe-card/RecipeCardButton'
import { Sparkles } from 'lucide-react'
import {
  normalizeEnabledOrderTypes,
  displayLabelForOrderType,
  colorForOrderTypeId,
  sanitizeOrderTypeHexColor,
  normalizeOrderTypeId,
} from '@/lib/orderTypeSettings'

/** Optional: last fire response JSON per station (KDS); printer stations use string — not rendered here */
export interface KOTStationPayload {
  station_id?: string
  station_name?: string
  output_type: 'kds' | 'printer'
  payload: unknown
}

export interface KOTCardProps {
  order: Order & {
    kot_first_sent_at?: string
    guest_count?: number
    server_name?: string
    table?: { table_number?: string; location?: string }
  }
  lastKotPayloads?: KOTStationPayload[]
  targetPrepMinutes?: number
  staleMinutes?: number
  /** Expo view: group the ticket's lines under their station with per-station
   * progress, so an expeditor sees what each station still owes. */
  groupByStation?: boolean
  /** Keyboard/bump-bar focus — draws a focus ring on the selected ticket. */
  focused?: boolean
  /** Order status is `ready` — at pass; hide bump and show pickup state */
  isAtPass?: boolean
  onItemTogglePrepared: (orderId: string, itemId: string, nextPrepared: boolean) => void
  /** Long-press handler for the intermediate "preparing" (started) state. */
  onItemSetStatus?: (orderId: string, itemId: string, status: 'sent' | 'preparing' | 'ready') => void
  onBump: (orderId: string) => Promise<void>
  bumpLoading?: boolean
  /** Clear ticket from KDS after food is picked up (order → served) */
  onPickedUp?: (orderId: string) => void
  pickedUpLoading?: boolean
  /** Recall from the at-pass lane back to the line. Only shown when order is ready. */
  onRecall?: (orderId: string) => void
  recallLoading?: boolean
  /**
   * Recipe cards (kitchen flow). When set, a line whose dish has a recipe
   * (`hasRecipe`) shows a Recipe button beside it. Both absent = the card is
   * exactly what it was.
   */
  onOpenRecipe?: (item: OrderItem) => void
  hasRecipe?: (productId: string) => boolean
  /**
   * F-AI-54: "Ask Bhookly AI about this dish" beside a line. Set only where
   * the copilot gate shows the bubble for this role; absent = no button.
   */
  onAskAI?: (item: OrderItem) => void
}

function isItemPrepared(item: OrderItem): boolean {
  return item.status === 'ready' || item.status === 'served'
}

function isVoided(item: OrderItem): boolean {
  return item.status === 'voided'
}

/**
 * Daily tickets: 20260413-001 → #001; legacy ORD202604139987 → #9987 (last 4).
 * F-MM-N23 marketplace series FP-20260413-001 → #FP-001 so the pass can tell a
 * Foodpanda ticket from the counter's own #001 of the same day.
 */
export function displayTicketNo(orderNumber: string): string {
  const daily = orderNumber.match(/^(?:([A-Z]{2,4})-)?(\d{8})-(\d{1,4})$/)
  if (daily) return `#${daily[1] ? `${daily[1]}-` : ''}${daily[3].padStart(3, '0')}`
  const ord = orderNumber.match(/^ORD(\d{8})(\d{4})$/i)
  if (ord) return `#${ord[2]}`
  return `#${orderNumber}`
}

/** 60s from fire: long enough for staff to notice, short enough to avoid "NEW forever". */
const NEW_BADGE_WINDOW_MS = 60_000

function useIsStillNew(kotSentAt: string | undefined, generation: number): boolean {
  const [stillNew, setStillNew] = useState<boolean>(() => {
    if (generation <= 1 || !kotSentAt) return false
    return Date.now() - new Date(kotSentAt).getTime() < NEW_BADGE_WINDOW_MS
  })
  useEffect(() => {
    if (generation <= 1 || !kotSentAt) {
      setStillNew(false)
      return
    }
    const elapsed = Date.now() - new Date(kotSentAt).getTime()
    if (elapsed >= NEW_BADGE_WINDOW_MS) {
      setStillNew(false)
      return
    }
    const t = window.setTimeout(
      () => setStillNew(false),
      NEW_BADGE_WINDOW_MS - elapsed,
    )
    return () => window.clearTimeout(t)
  }, [kotSentAt, generation])
  return stillNew
}

/** Ticket-level "NEW" flag: true for ~60s after the order's first KOT fire,
 * so a freshly-arrived (e.g. just-accepted online) ticket is unmistakable —
 * not just colour-coded fresh. Auto-clears via the same window as line badges. */
function useIsNewTicket(kotFirstSentAt: string | undefined): boolean {
  const [isNew, setIsNew] = useState<boolean>(() => {
    if (!kotFirstSentAt) return false
    return Date.now() - new Date(kotFirstSentAt).getTime() < NEW_BADGE_WINDOW_MS
  })
  useEffect(() => {
    if (!kotFirstSentAt) {
      setIsNew(false)
      return
    }
    const elapsed = Date.now() - new Date(kotFirstSentAt).getTime()
    if (elapsed >= NEW_BADGE_WINDOW_MS) {
      setIsNew(false)
      return
    }
    const t = window.setTimeout(() => setIsNew(false), NEW_BADGE_WINDOW_MS - elapsed)
    return () => window.clearTimeout(t)
  }, [kotFirstSentAt])
  return isNew
}

/** Map urgency tier to card chrome (border + header tint). */
const TIER_STYLE: Record<UrgencyTier, { border: string; header: string; clock: string }> = {
  fresh: {
    border: 'border-emerald-300 dark:border-emerald-700',
    header: 'bg-emerald-50/60 dark:bg-emerald-900/10',
    clock: 'text-emerald-700 dark:text-emerald-300',
  },
  warming: {
    border: 'border-amber-400 dark:border-amber-600',
    header: 'bg-amber-50 dark:bg-amber-900/20',
    clock: 'text-amber-800 dark:text-amber-200',
  },
  urgent: {
    border: 'border-red-500 dark:border-red-600',
    header: 'bg-red-50 dark:bg-red-900/20',
    clock: 'text-red-700 dark:text-red-300',
  },
  critical: {
    border: 'border-red-600 dark:border-red-500 shadow-[0_0_0_1px_rgba(220,38,38,0.5)]',
    header: 'bg-red-100 dark:bg-red-900/30',
    clock: 'text-red-800 dark:text-red-200',
  },
  stale: {
    border: 'border-muted-foreground/50 border-dashed',
    header: 'bg-muted',
    clock: 'text-muted-foreground',
  },
}

function OrderTypeBadge({ type }: { type: string }) {
  const { data: raw } = useQuery({
    queryKey: ENABLED_ORDER_TYPES_KEY,
    queryFn: () => fetchEnabledOrderTypes(),
  })
  const rows = normalizeEnabledOrderTypes(raw ?? [])
  const label = displayLabelForOrderType(type, rows)
  const hex = sanitizeOrderTypeHexColor(colorForOrderTypeId(rows, type) ?? '#64748b', '#64748b')
  const nid = normalizeOrderTypeId(type)
  const meta: Record<string, { icon: ElementType }> = {
    dine_in: { icon: Home },
    takeout: { icon: ShoppingBag },
    delivery: { icon: Truck },
  }
  const m = meta[nid] ?? { icon: Package, fallbackTone: 'bg-neutral-surface text-neutral-fg border-neutral-border' }
  const Icon = m.icon
  return (
    <Badge
      variant="outline"
      className={cn('gap-1 font-semibold border')}
      style={{
        borderColor: hex,
        backgroundColor: `color-mix(in srgb, ${hex} 14%, hsl(var(--card)))`,
        color: 'inherit',
      }}
    >
      <Icon className="h-3 w-3" style={{ color: hex }} aria-hidden />
      {label}
    </Badge>
  )
}

export function KOTCard({
  order,
  lastKotPayloads,
  targetPrepMinutes = 15,
  staleMinutes = 120,
  groupByStation = false,
  focused = false,
  isAtPass = false,
  onItemTogglePrepared,
  onItemSetStatus,
  onBump,
  bumpLoading,
  onPickedUp,
  pickedUpLoading,
  onRecall,
  recallLoading,
  onOpenRecipe,
  hasRecipe,
  onAskAI,
}: KOTCardProps) {
  // The line's Recipe button: only where recipes are on and the dish has one.
  const recipeFor = (item: OrderItem) =>
    onOpenRecipe && hasRecipe?.(item.product_id) ? onOpenRecipe : undefined
  const kotFirst = order.kot_first_sent_at
  const urgency = useKdsUrgencyTimer(kotFirst, order.created_at, targetPrepMinutes, staleMinutes)
  const style = TIER_STYLE[urgency.tier]
  const animate = urgency.tier === 'critical' && !isAtPass
  const isNewTicket = useIsNewTicket(kotFirst) && !isAtPass

  const tableLabel = order.table?.table_number ?? order.table_id ?? '—'
  const covers = order.guest_count ?? 0
  const items = order.items ?? []

  const { regularItems, voidedItems } = useMemo(() => {
    const reg: OrderItem[] = []
    const vo: OrderItem[] = []
    for (const it of items) {
      if (isVoided(it)) vo.push(it)
      else reg.push(it)
    }
    return { regularItems: reg, voidedItems: vo }
  }, [items])

  // Expo view: bucket lines by station (preserving server sort order) with a
  // ready/total tally per station so the expeditor sees who still owes what.
  const stationGroups = useMemo(() => {
    const order: string[] = []
    const map = new Map<string, OrderItem[]>()
    for (const it of regularItems) {
      const key = it.station_name ?? 'Kitchen'
      if (!map.has(key)) {
        map.set(key, [])
        order.push(key)
      }
      map.get(key)!.push(it)
    }
    return order.map((station) => {
      const groupItems = map.get(station)!
      return {
        station,
        items: groupItems,
        ready: groupItems.filter((i) => isItemPrepared(i)).length,
        total: groupItems.length,
      }
    })
  }, [regularItems])

  // Coursing: bucket lines by course (1=first) so a multi-course ticket shows
  // "Course 1 / Course 2…" dividers with per-course progress on the board.
  const courseGroups = useMemo(() => {
    const order: number[] = []
    const map = new Map<number, OrderItem[]>()
    for (const it of regularItems) {
      const c = it.course_number ?? 1
      if (!map.has(c)) {
        map.set(c, [])
        order.push(c)
      }
      map.get(c)!.push(it)
    }
    order.sort((a, b) => a - b)
    return order.map((course) => {
      const groupItems = map.get(course)!
      return {
        course,
        items: groupItems,
        ready: groupItems.filter((i) => isItemPrepared(i)).length,
        total: groupItems.length,
      }
    })
  }, [regularItems])
  const hasMultipleCourses = courseGroups.length > 1

  const allActivePrepared = useMemo(() => {
    // regularItems already excludes voided lines (they're split into voidedItems).
    // A ticket whose every line was voided has nothing left to cook — treat it as
    // "done" so Done/bump can clear it from the board. (The backend normally drops
    // fully-voided orders off the KDS feed entirely — F-KDS-A2 — but this keeps the
    // button live if such a card is on screen mid-refresh, instead of a dead grey
    // button with no rows to tap. A truly empty, still-loading card stays disabled.)
    if (regularItems.length === 0) return voidedItems.length > 0
    return regularItems.every((i) => isItemPrepared(i))
  }, [regularItems, voidedItems])

  const handleBump = useCallback(async () => {
    await onBump(order.id)
  }, [onBump, order.id])

  const isStale = urgency.tier === 'stale'
  const showDineIn = order.order_type === 'dine_in'

  return (
    <Card
      className={cn(
        'w-full flex flex-col border-2 transition-colors overflow-hidden',
        isAtPass
          ? 'border-emerald-400 bg-emerald-50/80 dark:bg-emerald-900/10 dark:border-emerald-600 shadow-md'
          : cn(style.border, 'bg-card'),
        // Brand-new ticket: sky ring so it pops out of the grid for ~60s.
        isNewTicket && 'ring-2 ring-sky-400 dark:ring-sky-500',
        // Keyboard/bump-bar focus takes precedence — thick indigo ring + offset.
        focused && 'ring-4 ring-indigo-500 ring-offset-2 ring-offset-background dark:ring-indigo-400',
        animate && 'animate-pulse',
      )}
    >
      <CardHeader
        className={cn(
          'pb-3 border-b space-y-2',
          isAtPass ? 'bg-emerald-100/60 dark:bg-emerald-900/20' : style.header,
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-2xl font-black tracking-tight text-foreground leading-none">
              {displayTicketNo(order.order_number)}
            </CardTitle>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
              {isNewTicket && (
                <Badge className="bg-sky-500 text-white gap-1 font-bold animate-pulse">
                  <Flame className="h-3 w-3" />
                  NEW
                </Badge>
              )}
              <OrderTypeBadge type={order.order_type} />
              {order.order_source === 'online' && (
                <Badge className="bg-indigo-600 text-white font-bold">
                  ONLINE
                </Badge>
              )}
              {showDineIn && (
                <>
                  <Badge variant="outline" className="font-semibold">
                    Table {tableLabel}
                  </Badge>
                  {covers > 0 && (
                    <Badge variant="secondary" className="font-semibold gap-1">
                      <Users className="h-3 w-3" />
                      {covers}
                    </Badge>
                  )}
                </>
              )}
              {!showDineIn && order.customer_name && (
                <span className="text-muted-foreground font-medium truncate max-w-[140px]">
                  {order.customer_name}
                </span>
              )}
            </div>
            {order.server_name && showDineIn && (
              <p className="mt-1 text-[11px] text-muted-foreground truncate">Server: {order.server_name}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className={cn('flex items-center justify-end gap-1 text-lg font-mono font-bold tabular-nums', style.clock)}>
              {isStale ? <Hourglass className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
              {urgency.elapsedLabel}
            </div>
            <div className="text-[10px] uppercase text-muted-foreground">since KOT</div>
            {urgency.tier === 'critical' && (
              <Badge className="mt-1 bg-red-600 text-white gap-1 text-[10px]">
                <Flame className="h-3 w-3" />
                Critical
              </Badge>
            )}
            {urgency.tier === 'urgent' && (
              <Badge className="mt-1 bg-red-500 text-white gap-1 text-[10px]">
                <Flame className="h-3 w-3" />
                Urgent
              </Badge>
            )}
            {isStale && (
              <Badge variant="outline" className="mt-1 text-[10px] gap-1">
                <AlertTriangle className="h-3 w-3" />
                Stale
              </Badge>
            )}
          </div>
        </div>

        {lastKotPayloads && lastKotPayloads.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {lastKotPayloads.map((k, i) => (
              <Badge key={i} variant="outline" className="text-[10px] gap-1 font-normal">
                {k.output_type === 'printer' ? <Printer className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                {k.station_name ?? 'Station'}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 flex flex-col pt-3 pb-3 space-y-2">
        <div className="space-y-1.5 flex-1">
          {regularItems.length === 0 && voidedItems.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No items</p>
          )}

          {groupByStation ? (
            stationGroups.map((g) => (
              <div key={g.station} className="space-y-1.5">
                <div className="flex items-center justify-between px-0.5">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {g.station}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px] tabular-nums',
                      g.ready === g.total
                        ? 'border-green-400 text-green-700 dark:text-green-300'
                        : 'text-muted-foreground',
                    )}
                  >
                    {g.ready}/{g.total} ready
                  </Badge>
                </div>
                {g.items.map((item) => (
                  <KOTLine
                    key={item.id}
                    item={item}
                    orderId={order.id}
                    disabled={isAtPass}
                    onToggle={onItemTogglePrepared}
                    onSetStatus={onItemSetStatus}
                    onOpenRecipe={recipeFor(item)}
                    onAskAI={onAskAI}
                  />
                ))}
              </div>
            ))
          ) : hasMultipleCourses ? (
            courseGroups.map((g) => (
              <div key={g.course} className="space-y-1.5">
                <div className="flex items-center justify-between px-0.5">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                    Course {g.course}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px] tabular-nums',
                      g.ready === g.total
                        ? 'border-green-400 text-green-700 dark:text-green-300'
                        : 'text-muted-foreground',
                    )}
                  >
                    {g.ready}/{g.total} ready
                  </Badge>
                </div>
                {g.items.map((item) => (
                  <KOTLine
                    key={item.id}
                    item={item}
                    orderId={order.id}
                    disabled={isAtPass}
                    onToggle={onItemTogglePrepared}
                    onSetStatus={onItemSetStatus}
                    onOpenRecipe={recipeFor(item)}
                    onAskAI={onAskAI}
                  />
                ))}
              </div>
            ))
          ) : (
            regularItems.map((item) => (
              <KOTLine
                key={item.id}
                item={item}
                orderId={order.id}
                disabled={isAtPass}
                onToggle={onItemTogglePrepared}
                onSetStatus={onItemSetStatus}
                onOpenRecipe={recipeFor(item)}
                onAskAI={onAskAI}
              />
            ))
          )}

          {voidedItems.map((item) => (
            <div
              key={item.id}
              className="rounded-lg border border-red-200 bg-red-50/70 dark:bg-red-900/10 dark:border-red-800 p-2 text-sm"
            >
              <div className="line-through text-red-900 dark:text-red-300 font-medium">
                {item.quantity}× {item.product?.name ?? 'Item'}
              </div>
              <Badge variant="destructive" className="mt-1 text-[10px]">
                VOIDED
              </Badge>
            </div>
          ))}
        </div>

        <div className="pt-2 border-t mt-auto space-y-2">
          {isAtPass ? (
            <div className="space-y-2">
              <div className="rounded-lg bg-emerald-600/10 border border-emerald-500/40 py-2 px-2 text-center">
                <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-200">At the pass</p>
                <p className="text-[10px] text-emerald-800/90 dark:text-emerald-300/90">Waiting for server pickup</p>
              </div>
              <div className="flex gap-2">
                {onPickedUp && (
                  <Button
                    type="button"
                    className="flex-1 h-10 text-sm font-semibold "
                    disabled={pickedUpLoading}
                    onClick={() => onPickedUp(order.id)}
                  >
                    {pickedUpLoading ? 'Updating…' : 'Picked up'}
                  </Button>
                )}
                {onRecall && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10"
                    disabled={recallLoading}
                    onClick={() => onRecall(order.id)}
                    aria-label="Recall to line"
                    title="Recall to line"
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              <Button
                className={cn(
                  'w-full h-11 text-base font-semibold',
                  allActivePrepared
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-border text-muted-foreground hover:bg-muted cursor-not-allowed',
                )}
                disabled={bumpLoading || !allActivePrepared}
                onClick={handleBump}
              >
                {bumpLoading ? 'Marking done…' : 'Done'}
              </Button>
              {!allActivePrepared && regularItems.length > 0 && (
                <p className="text-[11px] text-center text-muted-foreground">
                  Tap each item to mark prepared, then Done
                </p>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface KOTLineProps {
  item: OrderItem
  orderId: string
  disabled: boolean
  /** Opens the dish's recipe card; absent = no Recipe button on this line. */
  onOpenRecipe?: (item: OrderItem) => void
  /** Asks the copilot about this dish; absent = no Ask button. */
  onAskAI?: (item: OrderItem) => void
  onToggle: (orderId: string, itemId: string, nextPrepared: boolean) => void
  /** Long-press handler — toggles the intermediate "preparing" (started) state. */
  onSetStatus?: (orderId: string, itemId: string, status: 'sent' | 'preparing' | 'ready') => void
}

function KOTLine({ item, orderId, disabled, onToggle, onSetStatus, onOpenRecipe, onAskAI }: KOTLineProps) {
  const prepared = isItemPrepared(item)
  const preparing = item.status === 'preparing'
  const gen = (item as OrderItem & { kot_fire_generation?: number }).kot_fire_generation ?? 1
  const sentAt = (item as OrderItem & { kot_sent_at?: string }).kot_sent_at
  const stillNew = useIsStillNew(sentAt, gen)

  // Long-press toggles the "preparing" (started) state without finishing the
  // line. A press that fires the timer suppresses the subsequent click so a
  // long-press never accidentally also marks the item ready.
  const longPressTimer = useRef<number | null>(null)
  const longPressed = useRef(false)
  const cancelPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }
  const startPress = () => {
    if (disabled || !onSetStatus || prepared) return
    longPressed.current = false
    longPressTimer.current = window.setTimeout(() => {
      longPressed.current = true
      onSetStatus(orderId, item.id, preparing ? 'sent' : 'preparing')
    }, 450)
  }
  // Pop the checkbox + buzz + soft tick on every tap (check OR undo) so the
  // action registers the instant a greasy hand lands — independent of the
  // optimistic state flip and the network round-trip. The tactile confirmation
  // also discourages the uncertain double-taps the undo exists to fix.
  const boxRef = useRef<HTMLSpanElement | null>(null)
  const tapFeedback = (nextPrepared: boolean) => {
    // Web Animations API restarts reliably on rapid re-taps (a CSS class can't).
    boxRef.current?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(1)' }],
      { duration: 200, easing: 'ease-out' },
    )
    try {
      // Subtle on check, a short double-buzz on undo. No-op where unsupported (iPad/Safari).
      navigator.vibrate?.(nextPrepared ? 12 : [8, 28, 8])
    } catch {
      /* haptics best-effort */
    }
    kitchenSoundService.playTick(nextPrepared ? 'on' : 'off')
  }
  const handleClick = () => {
    if (longPressed.current) {
      longPressed.current = false
      return
    }
    const nextPrepared = !prepared
    tapFeedback(nextPrepared)
    onToggle(orderId, item.id, nextPrepared)
  }

  const row = (
    <button
      type="button"
      disabled={disabled}
      onClick={handleClick}
      onPointerDown={startPress}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onContextMenu={(e) => e.preventDefault()}
      className={cn(
        // p-3 + min-h keeps the whole row a comfortable ≥44px touch target
        // for greasy-hand taps; sizes scale with the per-device --ui-zoom.
        'w-full text-left rounded-lg border p-3 min-h-[3rem] transition-colors select-none touch-none',
        prepared
          ? 'border-green-400 bg-green-50 dark:bg-green-900/20 dark:border-green-700'
          : preparing
            ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/25 dark:border-amber-600'
            : stillNew
              ? 'border-amber-300 bg-amber-50/60 dark:bg-amber-900/15 dark:border-amber-700 hover:border-amber-400'
              : 'border-border bg-card hover:border-amber-300',
        disabled && 'opacity-80 cursor-not-allowed',
      )}
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <span
          ref={boxRef}
          className={cn(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border-2',
            prepared
              ? 'border-green-500 bg-green-500 text-white'
              : preparing
                ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                : 'border-input',
          )}
        >
          {prepared ? <CheckCircle className="h-6 w-6" /> : preparing ? <Flame className="h-5 w-5" /> : null}
        </span>
        <div className="min-w-0 flex-1">
          {/* Item names are read from across the kitchen — large + bold, with
              the quantity called out so multiples can't be missed. */}
          <div className="font-bold text-lg text-foreground leading-tight">
            <span className="text-xl font-black tabular-nums">{item.quantity}×</span>{' '}
            {item.product?.name ?? 'Item'}
          </div>
          {/* Chosen options (F-MOD-N6).
              Deliberately NOT the amber pill: amber is the established
              "exception, read this carefully" signal for a free-text note, and
              a modifier is the NORMAL case. Using one colour for both would
              blunt the distinction the cook relies on from across the kitchen.
              Subordinate to the item name, clearly above note weight. */}
          {(item.modifier_labels?.length ?? 0) > 0 && (
            <ul className="mt-0.5 space-y-0.5">
              {item.modifier_labels!.map((label, i) => (
                <li
                  key={`${label}-${i}`}
                  className="text-base font-semibold leading-snug text-foreground"
                >
                  • {label}
                </li>
              ))}
            </ul>
          )}
          {item.special_instructions && (
            <p className="mt-1 inline-block rounded-md bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-sm font-bold text-amber-900 dark:text-amber-200 leading-snug">
              {item.special_instructions}
            </p>
          )}
          {preparing && (
            <Badge className="mt-1 mr-1 bg-amber-500 text-amber-950 text-[10px] h-4 px-1.5 align-middle gap-1">
              <Flame className="h-2.5 w-2.5" />
              Cooking
            </Badge>
          )}
          {stillNew && (
            <Badge className="mt-1 ml-1 bg-amber-500 text-amber-950 text-[10px] h-4 px-1.5 align-middle">
              NEW
            </Badge>
          )}
        </div>
      </div>
    </button>
  )
  if (!onOpenRecipe && !onAskAI) return row
  // The Recipe and Ask buttons are SIBLINGS of the row, never children: the
  // row is a button (tap = ready, long press = cooking), a button cannot hold
  // a button, and a tap on either must never tick or start the line. They
  // stay usable on a ticket at the pass — reading changes nothing.
  return (
    <div className="flex items-start gap-1.5">
      <div className="min-w-0 flex-1">{row}</div>
      {onOpenRecipe && (
        <RecipeCardButton
          iconOnly
          className="mt-1"
          name={item.product?.name ?? 'Item'}
          onOpen={() => onOpenRecipe(item)}
        />
      )}
      {onAskAI && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onAskAI(item)
          }}
          aria-label={`Ask Bhookly AI about ${item.product?.name ?? 'this item'}`}
          title="Ask Bhookly AI"
          className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-primary hover:bg-muted"
        >
          <Sparkles className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  )
}
